/* @pareto-engineering/generator-front 1.0.12 */
import React, {
  useMemo,
} from 'react'

import styleNames from '@pareto-engineering/bem/exports'

import {
  AnimatedGradient,
  Page,
  useApp,
  Button,
  Title,
} from '@pareto-engineering/design-system'

import {
  Footer,
  NavigationBar,
  UseCaseCard,
  Steps,
  FAQ,
  ConversionPage,
  aiExpertSectionMap,
  TestimonialCard,
} from 'modules/components'

import {
  SITE,
  STATIC,
} from 'modules/urls'

const baseClassName = styleNames.base

const componentClassName = 'page-ai-trainer'

const coreValues = [
  {
    title      :'Industry leading pay',
    image      :'/misc/labelers-leading-pay.webp',
    description:(
      <>
        Earn
        {' '}
        <span className="x-highlighted c-x">
          industry-leading pay.
        </span>
        {' '}
        Get compensated for each completed task or hour you have worked. We offer
        {' '}
        <span className="x-highlighted c-x">
          payments twice a month
        </span>
        , ensuring you get compensated on time and transparently.
      </>
    ),
  },
  {
    title      :'Flexible hours',
    image      :'/misc/labelers-flexible-hours.webp',
    description:(
      <>
        Our
        {' '}
        <span className="x-highlighted c-x">
          project-based, remote, and flexible work
        </span>
        {' '}
        arrangements allow you to train AI without compromising on other aspects of your life.
        You can work on projects alongside your full-time job as a supplemental income source.
      </>
    ),
  },
  {
    title      :'Meaningful work and transparency',
    image      :'/misc/labelers-meaningful-work.webp',
    description:(
      <>
        We handpick projects that align with
        {' '}
        <span className="x-highlighted c-x">
          your expertise and interests.
        </span>
        {' '}
        This tailored approach ensures you&apos;re engaged in work you truly enjoy,
        allowing you to give your absolute best to projects that resonate with you.
      </>
    ),
  },
  {
    title      :'Develop your skills in AI training',
    image      :'/misc/labelers-long-term-career.webp',
    description:(
      <>
        We help you
        {' '}
        <span className="x-highlighted c-x">
          develop your skills
        </span>
        {' '}
        as an AI training expert across various projects, providing clear and prompt feedback.
        We encourage you to continuously improve your skills, ensuring you
        {' '}
        <span className="x-highlighted c-x">
          create an impact
        </span>
        {' '}
        on the next generation of AI models.
      </>
    ),
  },
]

const steps = [
  {
    title      :'Fill out our general interest survey',
    description:'Apply to join our network of expert-vetted labelers. We\'ll reach out if we think you\'re a good fit for our community and our project opportunities!',
  },
  {
    title      :'Complete our screening process',
    description:(
      <>
        We&apos;ll notify selected applicants about upcoming projects based on their expertise.
        If you are selected, get ready to showcase your skills through
        {' '}
        <span className="x-highlighted c-x">
          a fully paid trial.
        </span>
      </>
    ),
  },
  {
    title      :'Start earning!',
    description:(
      <>
        Get paid twice a month and enjoy
        {' '}
        <span className="x-highlighted c-x">
          competitive
        </span>
        {' '}
        (we mean it) pay for your hard work! We offer paid training to familiarize you with the
        tasks you&apos;ll be working on and tools you&apos;ll be using.
      </>
    ),
  },
]

const testimonials = [
  {
    name       :'Baobao Tian',
    role       :'Data Analyst',
    image      :'/testimonials/baobao-tian.webp',
    testimonial:"Working with Pareto.AI's Project Managers and mentors has been a truly enriching experience; their kindness and support are unparalleled. Professionally, my tenure here has significantly deepened my involvement in AI, providing a robust platform for growth.",
  },
  {
    name       :'Gilbert Kamau',
    role       :'Data Scientist',
    image      :'/testimonials/gilbert-kamau.webp',
    testimonial:'At Pareto.AI, labelers are valued far beyond being just another group of workers. The company demonstrates great consideration for its labelers, actively listening to their needs and embracing individuals from diverse nationalities, genders, ethnicities, and races. Pareto.AI celebrates their contributions and actively promotes inclusivity, ensuring everyone feels appreciated and respected.',
  },
  {
    name       :'Joseph Mburu',
    role       :'Network IT Specialist',
    image      :'/testimonials/joseph-mburu.webp',
    testimonial:"The payout process is efficient, reflecting Pareto.AI's commitment to contributors. Professionally, working here has significantly enhanced my skills, while personally, it has been a rewarding experience. What I cherish the most about being an annotator is the sense of collaboration and continuous learning.",
  },
  {
    name       :'Dr. Morissa Schwartz',
    role       :'CEO',
    image      :'/testimonials/morissa-schwartz.webp',
    testimonial:"Participating in Pareto.AI's project was an intellectually enriching experience, reigniting my passion for philosophy through engaging discussions with AI. It felt like conversing with a grad school professor, deepening my understanding and appreciation of philosophical concepts.",
  },
  {
    name       :'Knux',
    role       :'Software Engineer',
    image      :'/testimonials/knux.webp',
    testimonial:"I think what sets Pareto apart is that the company does an excellent job of producing LLM training data while adhering to all ethical standards. This is incredibly important to me. Much of the current AI training data is generated without any regard for ethics or user privacy, so it's refreshing to see Pareto maintain strict ethical guidelines.",
  },
]

const AITrainer = () => {
  const app = useApp()

  const helmetProps = useMemo(() => ({
    title           :'Work Remotely as an AI Trainer | Pareto.AI',
    meta_description:'Earn industry-leading pay while contributing to cutting-edge AI development as an AI trainer with Pareto.AI ',
    canonical       :SITE.AI_TRAINER,
    og_type         :'website',
    main_image      :`${app.SITE.CANONICAL}/misc/social-share.jpg`,
  }), [])

  const faqMap = useMemo(() => ([
    {
      question:'How flexible are the working hours?',
      answer  :'Our platform allows for flexible scheduling, allowing you to choose when and how much you work to suit your lifestyle.',
    },
    {
      question:'Can I apply from anywhere in the world?',
      answer  :'Absolutely! While some projects may have location-specific requirements, we offer opportunities available to all experts worldwide. We match applicants with projects that suit their skills and align with customer budgets.',
    },
    {
      question:'How will I get paid and how often are payments processed?',
      answer  :'Payments are processed on a weekly basis. We disburse payment through PayPal and Wise.',
    },
    {
      question:'What documents do I need to submit?',
      answer  :'We usually ask for a resumé, although we may require other documentation (such as medical or law degrees) to prove expertise for certain projects.',
    },
    {
      question:'Will I need to sign an NDA (Non-disclosure agreement?)',
      answer  :'Yes.',
    },
    {
      question:'What types of training or support do you offer to new data experts?',
      answer  :'We provide comprehensive training materials and ongoing support to ensure you feel confident and proficient in your tasks. You will also get hands-on mentorship by senior prompt engineers and AI experts.',
    },
    {
      question:'How are the tasks allocated, and can I choose the projects I work on?',
      answer  :'Tasks are allocated depending on researchers\' needs, keeping in mind both your preferences and expertise. In some projects, you\'ll have the autonomy to select specific tasks based on your interests and availability.',

    },
    {
      question:'What equipment or software do I need to perform tasks?',
      answer  :'Basic hardware such as a computer or laptop and a stable internet connection are necessary. We\'ll provide access to the required software in cases where it is needed.',
    },
    {
      question:'How does Pareto ensure safe working conditions for labelers?',
      answer  :(
        <>
          Pareto is committed to maintaining a respectful, ethical workplace.
          Our remote environment allows everyone, including our staff,
          to work in the comfort of their home.
          Beyond our community structure, we refuse to permit harassment
          and discrimination in any form.
          Finally, we abhor any system which disenfranchises its labelers.
          For more information, please read our
          {' '}
          <a href={app.EXTRA.CODE_OF_CONDUCT} target="_blank" rel="noopener noreferrer">
            Code of Conduct
          </a>
          {' '}
          and
          {' '}
          <a href={STATIC.MSSTATEMENT} target="_blank" rel="noopener noreferrer">
            Modern Slavery Statement.
          </a>
        </>
      ),
    },
  ]), [])

  return (
    <ConversionPage
      id="page-ai-trainer"
      conversionFormConfig={aiExpertSectionMap}
      mobileButtonText="Get started"
      desktopLaunchText="Apply to train AI."
      mobileLaunchText="Apply to train AI."
      itemScope
      itemType="https://schema.org/FAQPage"
      helmetProps={helmetProps}
      Background={AnimatedGradient}
      disableNav
      overrideGridProps="start-1 span-8 md-start-2 md-span-12"
      className={[
        baseClassName,
        componentClassName,
      ]
        .filter((e) => e)
        .join(' ')}
    >
      <NavigationBar />

      <Page.Section
        id="ai-trainer-hero"
        className="to-plain"
        isSpaced
      >
        <Title
          heading={(
            <>
              <span>
                Apply to join open projects as an
              </span>
              {' '}
              <span className="x-highlighted c-x">
                AI Trainer
              </span>
            </>
          )}
          className="span-8 md-start-2 md-span-6"
        />
        <img
          alt="map of world with markers for pareto employees"
          src="/misc/careers-map.webp"
          className="span-8 md-start-9 md-span-5"
          style={{
            gridRow  :'span 4',
            width    :'100%',
            objectFit:'cover',
          }}
        />
      </Page.Section>

      <Page.Section
        id="labelers-steps"
        className="x-background-far b-x"
        isSpaced
      >
        <p className="span-8 md-start-2 md-span-4 h1 remove-top-margin">
          <span className="x-highlighted c-x">
            How can I become an expert?
          </span>
        </p>
        <div className="span-8 md-start-8 md-span-6 grid">
          <Steps
            items={steps}
            className="span-grid"
          />
          <a
            href={app.EXTRA.TRAINER_SURVEY}
            target="_blank"
            rel="noopener noreferrer"
            className="md-start-2"
          >
            <Button
              isGradient
              color="interactive"
            >
              Apply to train AI
            </Button>
          </a>
        </div>
      </Page.Section>

      <Page.Section
        id="ai-trainer-expectations"
        className="from-plain"
      >
        <p className="h1 span-8 md-start-2 md-span-7">
          <span className="x-highlighted c-x">
            What can you expect when working with Pareto.AI?
          </span>
        </p>
        <div className="span-8 md-start-2 md-span-12 grid">
          {
            coreValues.map((useCase) => (
              <UseCaseCard
                id={useCase.title}
                {...useCase}
                className="span-8 md-span-6"
              />
            ))
          }
        </div>
      </Page.Section>

      <Page.Section
        id="ai-trainer-testimonials"
        className="to-plain"
        isSpaced
      >
        <p className="h1 md-start-2 md-span-8 span-8">
          <span className="x-highlighted c-x">
            What our experts have to say
          </span>
        </p>
        <TestimonialCard.Group
          id="testimonials"
          className="span-8 md-start-2 md-span-12"
          desktopType="grid"
          type="snap-scroller"
        >
          {
            testimonials.map((testimonial, i) => (
              <TestimonialCard
                className="span-7 md-span-6 manual-mobile-only"
                id={`testimonial-${i + 1}`}
                {...testimonial}
              />
            ))
          }
        </TestimonialCard.Group>
      </Page.Section>

      <Page.Section
        id="labelers-faq"
        className="from-plain"
        isSpaced
      >
        <p className="span-8 md-start-4 md-span-8 h1 remove-margin">
          <span className="x-highlighted c-x">
            Frequently asked questions
          </span>
        </p>
        <FAQ className="span-8 md-start-4 md-span-8 s1">
          {faqMap.map((item) => (
            <FAQ.Item question={item.question} titleAs="h3">
              <p>
                {item.Component ? <item.Component /> : item.answer}
              </p>
            </FAQ.Item>
          ))}
        </FAQ>
      </Page.Section>

      <Page.Section
        id="ai-trainer-apply"
        className="to-plain"
        isSpaced
      >
        <div
          className="span-8 md-start-4 md-span-8 centered-column"
          style={{
            marginBottom:'var(--theme-default-section-spacing)',
          }}
        >
          <p className="h1">
            <span className="x-highlighted c-x">
              Apply to become an AI Trainer
            </span>
          </p>
          <a
            href={app.EXTRA.TRAINER_SURVEY}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              isGradient
              color="interactive"
              style={{
                marginBottom:'var(--gap)',
              }}
            >
              Apply to train AI
            </Button>
          </a>
        </div>
      </Page.Section>

      <Footer />
    </ConversionPage>
  )
}

export default AITrainer
